'use client';

import { c as _c } from "react/compiler-runtime";
import type { AbstractIntlMessages } from 'next-intl';
import { NextIntlClientProvider } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import type { Locale } from '@/i18n/config';
import { defaultTimeZone } from '@/i18n/locales';
import { logger } from '@/lib/telemetry/logger';
import { useLocaleStore } from '@/stores/locale';
const LOCALE_LOADERS: Record<Locale, () => Promise<AbstractIntlMessages>> = {
  en: () => import('@/i18n/locales/en.json').then(m => m.default as AbstractIntlMessages),
  es: () => import('@/i18n/locales/es.json').then(m => m.default as AbstractIntlMessages),
  fr: () => import('@/i18n/locales/fr.json').then(m => m.default as AbstractIntlMessages),
  zh: () => import('@/i18n/locales/zh.json').then(m => m.default as AbstractIntlMessages),
  ja: () => import('@/i18n/locales/ja.json').then(m => m.default as AbstractIntlMessages),
  ko: () => import('@/i18n/locales/ko.json').then(m => m.default as AbstractIntlMessages),
  pt: () => import('@/i18n/locales/pt.json').then(m => m.default as AbstractIntlMessages),
  de: () => import('@/i18n/locales/de.json').then(m => m.default as AbstractIntlMessages),
  hi: () => import('@/i18n/locales/hi.json').then(m => m.default as AbstractIntlMessages),
  ru: () => import('@/i18n/locales/ru.json').then(m => m.default as AbstractIntlMessages),
  tr: () => import('@/i18n/locales/tr.json').then(m => m.default as AbstractIntlMessages),
  it: () => import('@/i18n/locales/it.json').then(m => m.default as AbstractIntlMessages),
  id: () => import('@/i18n/locales/id.json').then(m => m.default as AbstractIntlMessages),
  vi: () => import('@/i18n/locales/vi.json').then(m => m.default as AbstractIntlMessages),
  nl: () => import('@/i18n/locales/nl.json').then(m => m.default as AbstractIntlMessages),
  pl: () => import('@/i18n/locales/pl.json').then(m => m.default as AbstractIntlMessages),
  th: () => import('@/i18n/locales/th.json').then(m => m.default as AbstractIntlMessages),
  uk: () => import('@/i18n/locales/uk.json').then(m => m.default as AbstractIntlMessages),
  ro: () => import('@/i18n/locales/ro.json').then(m => m.default as AbstractIntlMessages),
  tl: () => import('@/i18n/locales/tl.json').then(m => m.default as AbstractIntlMessages),
  sv: () => import('@/i18n/locales/sv.json').then(m => m.default as AbstractIntlMessages)
};
async function loadMessages(locale: Locale): Promise<AbstractIntlMessages> {
  return LOCALE_LOADERS[locale]();
}
interface LocaleProviderProps {
  initialLocale: Locale;
  initialMessages: AbstractIntlMessages;
  children: React.ReactNode;
}
export function LocaleProvider(t0) {
  const $ = _c(9);
  const {
    initialLocale,
    initialMessages,
    children
  } = t0;
  const storeLocale = useLocaleStore(_temp);
  const [messages, setMessages] = useState(initialMessages);
  const [activeLocale, setActiveLocale] = useState(initialLocale);
  const hasMountedRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== activeLocale || $[1] !== initialLocale || $[2] !== storeLocale) {
    t1 = () => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        if (storeLocale !== initialLocale) {
          let cancelled = false;
          const syncOnMount = async function syncOnMount() {
            ;
            try {
              const newMessages = await loadMessages(storeLocale);
              if (!cancelled) {
                setMessages(newMessages);
                setActiveLocale(storeLocale);
              }
            } catch (t3) {
              const err = t3;
              logger.warn("Failed to load locale messages on mount sync", {
                locale: storeLocale,
                error: err instanceof Error ? err.message.slice(0, 200) : String(err)
              });
            }
          };
          syncOnMount();
          return () => {
            cancelled = true;
          };
        }
        return;
      }
      if (storeLocale === activeLocale) {
        return;
      }
      let cancelled_0 = false;
      const loadAndSet = async function loadAndSet() {
        ;
        try {
          const newMessages_0 = await loadMessages(storeLocale);
          if (!cancelled_0) {
            setMessages(newMessages_0);
            setActiveLocale(storeLocale);
          }
        } catch (t4) {
          const err_0 = t4;
          logger.warn("Failed to load locale messages", {
            locale: storeLocale,
            error: err_0 instanceof Error ? err_0.message.slice(0, 200) : String(err_0)
          });
        }
      };
      loadAndSet();
      return () => {
        cancelled_0 = true;
      };
    };
    t2 = [storeLocale, activeLocale, initialLocale];
    $[0] = activeLocale;
    $[1] = initialLocale;
    $[2] = storeLocale;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== activeLocale || $[6] !== children || $[7] !== messages) {
    t3 = <NextIntlClientProvider locale={activeLocale} messages={messages} timeZone={defaultTimeZone}>{children}</NextIntlClientProvider>;
    $[5] = activeLocale;
    $[6] = children;
    $[7] = messages;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(s) {
  return s.locale;
}